import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, ArrowRight, Clock, Download, CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";import { toast } from "sonner";
import { leadApi, API, formatApiError } from "@/lib/api";
import { PdfUpload } from "@/components/PdfUpload";
import { LOGO_URL } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";

const inputCls = "w-full bg-[#050505] border border-[#2A2A2A] px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FF4D00] transition-colors placeholder:text-[#555]";

const TEMPLATES = [
  { slot: "w9", label: "W-9 Form" },
  { slot: "noncompete", label: "Non-Compete Agreement" },
  { slot: "conduct", label: "Conduct & Policy (Ethics · At-Will)" },
];

export default function ApplicantStatus() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [lead, setLead] = useState(null);
  const [checking, setChecking] = useState(true);

  // OTP flow
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [phase, setPhase] = useState("email"); // email | code
  const [busy, setBusy] = useState(false);

  const loadMe = useCallback(async () => {
    try { const { data } = await leadApi.get("/leads/me"); setLead(data.lead); }
    catch { localStorage.removeItem("splash_lead_token"); setLead(null); }
    finally { setChecking(false); }
  }, []);

  useEffect(() => {
    // Magic-link auto-login: ?token=<lead token>
    const magic = params.get("token");
    if (magic) {
      localStorage.setItem("splash_lead_token", magic);
      params.delete("token");
      setParams(params, { replace: true });
      loadMe();
      return;
    }
    if (localStorage.getItem("splash_lead_token")) loadMe();
    else setChecking(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMe]);

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try { await leadApi.post("/leads/otp/request", { email }); toast.success("If an application exists, a code is on its way"); setPhase("code"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!code) return;
    setBusy(true);
    try {
      const { data } = await leadApi.post("/leads/otp/verify", { email, code });
      localStorage.setItem("splash_lead_token", data.token);
      setLead(data.lead);
      toast.success("Signed in");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const signOut = () => { localStorage.removeItem("splash_lead_token"); setLead(null); setPhase("email"); setCode(""); };

  if (checking) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF4D00]" /></div>;

  return (
    <div className="min-h-screen bg-[#050505] text-white grid-noise">
      <header className="border-b border-[#2A2A2A]">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/"><img src={LOGO_URL} alt="Splash Creative Hub" className="w-[140px] h-auto object-contain" /></Link>
          {lead ? (
            <button data-testid="status-signout-btn" onClick={signOut} className="text-sm text-[#A1A1AA] hover:text-white transition-colors">Sign out</button>
          ) : (
            <Link to="/" className="text-sm text-[#A1A1AA] hover:text-white inline-flex items-center gap-2 transition-colors"><ArrowLeft className="w-4 h-4" /> Home</Link>
          )}
        </div>
      </header>

      <main className="max-w-[1000px] mx-auto px-6 lg:px-10 py-16">
        {!lead ? (
          <div className="max-w-md" data-testid="otp-auth">
            <div className="text-xs uppercase tracking-[0.25em] text-[#FF4D00] mb-3">Application Status</div>
            <h1 className="font-bebas text-5xl mb-2">CHECK YOUR STATUS</h1>
            <p className="text-sm text-[#A1A1AA] mb-8">Enter the email you applied with and we'll send you a one-time code — no password needed.</p>
            {phase === "email" ? (
              <form onSubmit={requestCode} className="space-y-4" data-testid="otp-email-form">
                <input data-testid="otp-email-input" type="email" className={inputCls} placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required />
                <button data-testid="otp-request-btn" disabled={busy} className="w-full bg-[#FF4D00] text-white py-3 hover:bg-[#E64500] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send me a code
                </button>
                <p className="text-xs text-[#555]">Haven't applied yet? <Link to="/join" className="text-[#FF4D00] hover:underline">Apply here</Link>.</p>
              </form>
            ) : (
              <form onSubmit={verifyCode} className="space-y-4" data-testid="otp-code-form">
                <p className="text-sm text-[#A1A1AA]">We sent a 6-digit code to <strong className="text-white">{email}</strong>.</p>
                <input data-testid="otp-code-input" inputMode="numeric" maxLength={6} className={`${inputCls} font-mono tracking-[0.5em] text-center text-lg`} placeholder="••••••" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required />
                <button data-testid="otp-verify-btn" disabled={busy} className="w-full bg-[#FF4D00] text-white py-3 hover:bg-[#E64500] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Verify & continue
                </button>
                <button type="button" data-testid="otp-back-btn" onClick={() => setPhase("email")} className="w-full border border-[#2A2A2A] text-[#A1A1AA] py-3 hover:bg-[#111111] hover:text-white transition-colors text-sm">Use a different email</button>
              </form>
            )}
          </div>
        ) : (
          <StatusDashboard lead={lead} onRefresh={loadMe} setUser={setUser} navigate={navigate} />
        )}
      </main>
    </div>
  );
}

function StateHeader({ tag, title, tone = "#FF4D00" }) {
  return (
    <>
      <div className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: tone }}>{tag}</div>
      <h1 className="font-bebas text-5xl sm:text-6xl mb-4">{title}</h1>
    </>
  );
}

function StatusDashboard({ lead, onRefresh, setUser, navigate }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid={`status-${lead.state}`}>
      <p className="text-sm text-[#A1A1AA] mb-8">Signed in as <span className="text-white">{lead.name}</span> · {lead.email}</p>

      {lead.state === "under_review" && (
        <div className="border border-[#2A2A2A] bg-[#111111] p-10">
          <StateHeader tag="Step 1 of 4" title="APPLICATION UNDER REVIEW" />
          <div className="flex items-start gap-4 mt-4">
            <div className="w-12 h-12 rounded-full bg-[#FF4D0020] flex items-center justify-center shrink-0"><Clock className="w-6 h-6 text-[#FF4D00]" /></div>
            <p className="text-sm text-[#A1A1AA] leading-relaxed max-w-xl">
              Your application is in the hands of our team. We review every creative by hand to keep the Splash Team elite. You'll get an email the moment your status changes — check back here anytime.
            </p>
          </div>
        </div>
      )}

      {lead.state === "approved_for_onboarding" && <OnboardingStep lead={lead} onRefresh={onRefresh} />}

      {lead.state === "onboarding_submitted" && (
        <div className="border border-[#2A2A2A] bg-[#111111] p-10">
          <StateHeader tag="Step 3 of 4" title="ONBOARDING UNDER REVIEW" />
          <div className="flex items-start gap-4 mt-4">
            <div className="w-12 h-12 rounded-full bg-[#FF4D0020] flex items-center justify-center shrink-0"><ShieldCheck className="w-6 h-6 text-[#FF4D00]" /></div>
            <p className="text-sm text-[#A1A1AA] leading-relaxed max-w-xl">
              Thanks — we've received your signed documents. Our team is giving them a final review. You're almost there; we'll email you as soon as you're fully approved.
            </p>
          </div>
        </div>
      )}

      {lead.state === "fully_approved" && <FinalizeStep setUser={setUser} navigate={navigate} />}
    </motion.div>
  );
}

function OnboardingStep({ lead, onRefresh }) {
  const [avail, setAvail] = useState({});
  const [w9, setW9] = useState(lead.w9_file_id ? { id: lead.w9_file_id, original_filename: "W-9.pdf" } : null);
  const [nc, setNc] = useState(lead.noncompete_file_id ? { id: lead.noncompete_file_id, original_filename: "Non-Compete.pdf" } : null);
  const [cp, setCp] = useState(lead.conduct_file_id ? { id: lead.conduct_file_id, original_filename: "Conduct.pdf" } : null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { leadApi.get("/content/onboarding-templates").then(({ data }) => setAvail(data)).catch(() => {}); }, []);

  const submit = async () => {
    if (!w9 || !nc || !cp) { toast.error("Upload all three completed documents"); return; }
    setSubmitting(true);
    try {
      await leadApi.post("/leads/onboarding", { w9_file_id: w9.id, noncompete_file_id: nc.id, conduct_file_id: cp.id });
      toast.success("Documents submitted for review");
      onRefresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-8" data-testid="onboarding-step">
      <div>
        <StateHeader tag="Step 2 of 4 · Approved to onboard" title="COMPLETE YOUR ONBOARDING" />
        <p className="text-sm text-[#A1A1AA] max-w-2xl">Download each document, sign it, and upload the completed PDF below. Once all three are in, we'll do a final review.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {TEMPLATES.map((t) => (
          <div key={t.slot} className="border border-[#2A2A2A] bg-[#111111] p-5">
            <div className="font-bebas text-xl tracking-wide mb-2">{t.label}</div>
            {avail[t.slot] ? (
              <a data-testid={`template-download-${t.slot}`} href={`${API}/content/onboarding-templates/${t.slot}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm text-[#FF4D00] hover:underline"><Download className="w-4 h-4" /> Download</a>
            ) : (
              <span className="text-xs text-[#555]">Template coming soon</span>
            )}
          </div>
        ))}
      </div>

      <div className="border border-[#FF4D00]/40 bg-[#FF4D00]/[0.06] p-5 flex items-start gap-4" data-testid="security-note">
        <ShieldCheck className="w-6 h-6 text-[#FF4D00] shrink-0 mt-0.5" />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#FF4D00] mb-1">Security Commitment</div>
          <p className="text-sm text-[#D4D4D8] leading-relaxed">Your privacy is our priority. All sensitive documents, including W9s and resumes, are encrypted at rest and in transit. Access is strictly limited to authorized administrators for payroll and compliance purposes only.</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1">Completed W-9</div>
          <PdfUpload testId="upload-w9" category="w9" endpoint="/leads/upload" apiClient={leadApi} value={w9} onUploaded={setW9} label="Signed W-9 (PDF)" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1">Completed Non-Compete</div>
          <PdfUpload testId="upload-noncompete" category="noncompete" endpoint="/leads/upload" apiClient={leadApi} value={nc} onUploaded={setNc} label="Signed Non-Compete (PDF)" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1">Completed Conduct & Policy</div>
          <PdfUpload testId="upload-conduct" category="conduct" endpoint="/leads/upload" apiClient={leadApi} value={cp} onUploaded={setCp} label="Signed Conduct & Policy (PDF)" />
        </div>
      </div>

      <button data-testid="onboarding-submit-btn" onClick={submit} disabled={submitting} className="inline-flex items-center gap-2 bg-[#FF4D00] text-white px-7 py-3.5 text-sm hover:bg-[#E64500] transition-colors disabled:opacity-60">
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Submit for review <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function FinalizeStep({ setUser, navigate }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const finalize = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      const { data } = await leadApi.post("/leads/finalize", { password });
      localStorage.removeItem("splash_lead_token");
      localStorage.setItem("splash_token", data.token);
      setUser(data.user);
      toast.success("Account created — welcome to the Splash Team!");
      navigate("/portal");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-[#2A2A2A] bg-[#111111] p-10 max-w-lg" data-testid="finalize-step">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-[#10B98120] flex items-center justify-center"><CheckCircle2 className="w-6 h-6 text-[#10B981]" /></div>
        <div className="text-xs uppercase tracking-[0.25em] text-[#10B981]">Step 4 of 4 · Approved</div>
      </div>
      <h1 className="font-bebas text-5xl mb-3 flex items-center gap-3">APPLICATION APPROVED <Sparkles className="w-7 h-7 text-[#FF4D00]" /></h1>
      <p className="text-sm text-[#A1A1AA] mb-8">You're officially part of the Splash Team. Set a permanent password to finalize your account and unlock your creative portal.</p>
      <form onSubmit={finalize} className="space-y-4">
        <input data-testid="finalize-password-input" type="password" className={inputCls} placeholder="Create a password (min 8 chars)" value={password} onChange={(e) => setPassword(e.target.value)} required />
        <input data-testid="finalize-confirm-input" type="password" className={inputCls} placeholder="Confirm password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        <button data-testid="finalize-submit-btn" disabled={busy} className="w-full bg-[#FF4D00] text-white py-3.5 hover:bg-[#E64500] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Finalize account & enter portal <ArrowRight className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
